import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import type { Store } from "../../../types/store";
import { EditIcon, XCircle } from "lucide-react";
// import { useAppDispatch } from "../../../hooks";
import { useEmployees } from "../../../hooks/useEmployees";
import { useStores } from "../../../hooks/useStores";
import UpdateStoreBasicInfoModal from "../UpdateStoreBasicInfoModal";
import UpdateStoreManagerInfoModal from "../UpdateStoreManagerInfoModal";

interface Props {
  open: boolean;
  initial?: Store | null;
  onClose: () => void;
  onSave: (s: Store) => void;
}

const StoreSchema = Yup.object().shape({
  name: Yup.string().required("Store name is required"),
  location: Yup.string().optional(),
  contactInfo: Yup.string().optional(),
  managerId: Yup.string().nullable(),
});

const AddEditStoreModal: React.FC<Props> = ({ open, initial, onClose, onSave }) => {
  if (!open) return null;

  // const dispatch = useAppDispatch();
  const { employees, error, loading } = useEmployees();
  const { adding } = useStores();

  const [openEditStoreBasicInfoModal, setOpenEditStoreBasicInfoModal] = useState(false);
  const [openEditStoreManagerInfoModal, setOpenEditStoreManagerInfoModal] = useState(false);

  const defaultValues = initial ?? {
    id: "",
    name: "",
    location: "",
    contactInfo: "",
    managers: [],
    managerId: "",
    employeeCount: 0,
    salesCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const handleSubmit = (values: Store) => {
    const payload: Store = {
      ...values,
      id: values.id || `s-${Date.now()}`,
      updatedAt: new Date().toISOString(),
      createdAt: values.createdAt || new Date().toISOString(),
    };
    onSave(payload);
  };


  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-lg w-full max-w-xl p-4 max-h-screen overflow-y-scroll"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-3">
            <h3 className="text-lg text-left font-bold mb-4">
              {initial ? "Store Details" : "Add Store"}
            </h3>
            <div>
              <XCircle
                onClick={onClose}
                color="red"
                className="hover:cursor-pointer"
              />
            </div>
          </div>

          <Formik
            initialValues={defaultValues}
            validationSchema={StoreSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="grid gap-3">
                {/* BASIC INFORMATION */}
                {initial ? (
                  <div>
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-700">Basic Store Information</h4>
                      <button
                        type="button"
                        className="modal-btn-primary"
                        onClick={() => setOpenEditStoreBasicInfoModal(true)}
                      >
                        <EditIcon />
                        Edit
                      </button>
                    </div>

                    <div className="form-label">
                      <p className="form-label">Store Name</p>
                      <p className="font-medium form-input">{initial.name}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-3">
                      <div className="form-label">
                        <p className="form-label">Location</p>
                        <p className="font-medium form-input">{initial.location || "N/A"}</p>
                      </div>
                      <div className="form-label">
                        <p className="form-label">Contact Info</p>
                        <p className="font-medium form-input">{initial.contactInfo}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Store Name */}
                    <label className="form-label">
                      Store Name
                      <Field
                        name="name"
                        className="form-input mt-1 w-full"
                        placeholder="Enter store name"
                      />
                      {errors.name && touched.name && (
                        <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                      )}
                    </label>

                    <div className="flex md:flex-row sm:flex-col justify-between gap-2 mt-3">
                      {/* Location */}
                      <label className="form-label w-full">
                        Location
                        <Field
                          name="location"
                          className="form-input mt-1 w-full"
                          placeholder="Store location"
                        />
                      </label>

                      {/* Contact Info */}
                      <label className="form-label w-full">
                        Contact Info
                        <Field
                          name="contactInfo"
                          className="form-input mt-1 w-full"
                          placeholder="Phone or email"
                        />
                      </label>
                    </div>
                  </div>
                )}

                {initial ? (
                  <>
                    <div className="flex items-center justify-between mt-4">
                      <h4 className="font-semibold text-gray-700">Manager Information</h4>
                      <button
                        type="button"
                        className="modal-btn-primary"
                        onClick={() => {
                          setOpenEditStoreManagerInfoModal(true)
                        }}
                      >
                        <EditIcon />
                        Edit
                      </button>
                    </div>
                    <div className="text-left">
                      <p className="form-label mb-2">Managers</p>
                      <div className="flex flex-wrap">
                        {initial.managers && initial.managers.length > 0 ? (
                          initial.managers.map((manager) => (
                            <div key={manager.id} className="tile px-6 me-2 mb-2">
                              {manager.name || "Unnamed Manager"}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-400">No managers assigned</p>
                        )}
                      </div>

                    </div>
                  </>
                ) : (
                  <>
                    <label className="form-label">
                      Manager
                      <Field
                        as="select"
                        name="managerId"
                        className="form-input mt-1 w-full"
                      >
                        <option value="">Select a manager</option>
                        {employees &&
                          employees.map((emp) => {
                            const fullName = [
                              emp.firstname,
                              emp.othername,
                              emp.lastname,
                            ]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <option key={emp.id} value={emp.id}>
                                {fullName}
                              </option>
                            );
                          })}
                      </Field>
                    </label>

                    {loading && <p className="text-sm text-gray-500 mt-1">Loading managers...</p>}
                    {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
                  </>
                )}

                {/* Buttons */}
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-md border"
                  >
                    Cancel
                  </button>
                  {!initial && (
                    <button
                      type="submit"
                      className="px-4 py-2 rounded-md bg-primary text-white"
                      disabled={adding}
                    >
                      {adding ? (
                        <div className="flex items-center gap-2">
                          <span className="loader"></span>
                          <span>Saving</span>
                        </div>
                      ) : (
                        <div>Save Store</div>
                      )}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {initial && (
        <UpdateStoreBasicInfoModal
          store={initial}
          isOpen={openEditStoreBasicInfoModal}
          onClose={() => setOpenEditStoreBasicInfoModal(false)}
          onComplete={onClose}
        />
      )}

      {initial && (
        <UpdateStoreManagerInfoModal
          isOpen={openEditStoreManagerInfoModal}
          onClose={() => setOpenEditStoreManagerInfoModal(false)}
          store={initial}
          employees={employees.filter((employee) => {
            return employee.assignedRoles?.some(role => role.name === "Manager");
          })}
          onComplete={onClose}
        />
      )}

    </>
  );
};

export default AddEditStoreModal;
